import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Tooltip from '@material-ui/core/Tooltip';
import IconButton from '@material-ui/core/IconButton';
import Box from '@material-ui/core/Box';
import CloudDownloadIcon from '@material-ui/icons/CloudDownload';
import DataTable from 'components/Table/DataTable';
import { getReportFilesByFacilityByCreatedAt } from 'graphql/queries';
import ReportFileEditButton from './ReportFileEditButton';
import cache from 'utilities/cache';
import { download } from 'utilities/file';
import { Storage } from 'aws-amplify';
import { toastr } from 'react-redux-toastr';
import moment from 'moment-timezone';
import { TIME_ZONE } from '@silvergatedelivery/constants';
import { getClientOrganizationByClient, getOrganization } from './queries';
import { request } from 'utilities/graph';

const past = moment().add(-30, 'days').tz(TIME_ZONE).format('YYYY-MM-DD');
const todayDate = moment().tz(TIME_ZONE).format('YYYY-MM-DD');

export default function ReportFileTable({
  title = '報表上傳',
  description,
  ...props
}) {
  const [indexes, setIndexes] = useState();
  const [queryParams, setQueryParams] = useState();
  const [isLoading, setIsLoading] = useState(false);
  const [organizations, setOrganizations] = useState([]);

  const clientName = cache.get('app:name');
  const facilityId = cache.get('app:facilityId');

  useEffect(() => {
    if (!facilityId) return;

    (async () => {
      const { data: { getClientOrganizationByClient: clientOrganization } } = await request(getClientOrganizationByClient, {
        clientId: facilityId,
      });
      const organizations = await Promise.all(clientOrganization.items.map(async (item) => {
        const { data: { getOrganization: organization } } = await request(getOrganization, {
          id: item.organizationId,
        });
        return organization;
      }));
      setOrganizations(organizations);
    })();
  }, []);

  const fileDownload = async (fileS3Key) => {
    setIsLoading(true);
    try {
      const url = await Storage.get(fileS3Key);
      const fileResponse = await fetch(url);
      if (!fileResponse.ok) {
        throw new Error('下載檔案失敗');
      }
      const blob = await fileResponse.blob();
      const fileName = fileS3Key.split('/').pop();
      download(fileName, blob);
    } catch (e) {
      console.error(e.message);
      toastr.error(e.message);
    } finally {
      setIsLoading(false);
    }
  };

  const columns = [
    {
      name: 'id',
      label: '編號',
      options: {
        display: false,
        filter: false,
        sort: true,
      },
    },
    {
      name: 'name',
      label: '報表名稱',
      options: {
        display: true,
        filter: false,
        sort: true,
      },
    },
    {
      name: 'month',
      label: '月份',
      options: {
        display: true,
        filter: true,
        sort: true,
      },
    },
    {
      name: 'organizationId',
      label: '組織',
      options: {
        display: true,
        filter: false,
        sort: true,
        customBodyRender(organizationId) {
          const organization = organizations.find((o) => o.id === organizationId);
          return organization?.name || organizationId;
        },
      },
    },
    {
      name: 'category',
      label: '類別',
      options: {
        display: true,
        filter: true,
        sort: true,
      },
    },
    {
      name: 'note',
      label: '備註',
      options: {
        display: true,
        filter: false,
        sort: false,
      },
    },
    {
      name: 'createdBy',
      label: '創立者',
      options: {
        display: false,
        filter: false,
        sort: true,
      },
    },
    {
      name: 'updatedBy',
      label: '更新者',
      options: {
        display: false,
        filter: false,
        sort: true,
      },
    },
    {
      name: 'createdAt',
      label: '創立於',
      type: 'datetime',
      options: {
        display: false,
        filter: false,
        sort: true,
      },
    },
    {
      name: 'updatedAt',
      label: '更新於',
      type: 'datetime',
      options: {
        display: false,
        filter: false,
        sort: true,
      },
    },
    {
      name: 'fileS3Key',
      label: ' ',
      options: {
        display: 'always',
        filter: false,
        sort: false,
        download: false,
        print: false,
        customBodyRender(fileS3Key) {
          return (
            <Box display="flex" flexDirection="row" alignItems="top">
              <Tooltip
                title={'下載報表'}
                placement="bottom"
              >
                <Box display="flex" flexDirection="column" alignItems="center" p={1}>
                  <IconButton
                    onClick={async () => {
                      await fileDownload(fileS3Key);
                    }}
                    disabled={isLoading}
                  >
                    {<CloudDownloadIcon />}
                  </IconButton>
                </Box>
              </Tooltip>
            </Box>
          );
        },
      },
    },
  ];

  useEffect(() => {
    setIndexes([
      {
        name: 'getReportFilesByFacilityByCreatedAt',
        label: '按上傳時間查詢',
        partitionKey: 'facilityId',
        sortKey: 'createdAt',
        fields: [{
          label: '機構',
          key: 'facilityId',
          type: 'select',
          options: [{ value: facilityId, label: clientName }],
        }, {
          label: '上傳時間',
          key: 'createdAt',
          type: 'datetime',
        }],
        operation: getReportFilesByFacilityByCreatedAt,
      },
    ]);
    setQueryParams({
      facilityId,
      createdAt: {
        from: past,
        to: todayDate,
      },
    });
  }, []);


  if (!indexes || !queryParams) return null;

  return (
    <React.Fragment>
      <DataTable
        indexes={indexes}
        queryDefaultParams={queryParams}
        title={title}
        description={description}
        columns={columns}
        editButton={(props) => <ReportFileEditButton {...props} organizations={organizations} />}
        {...props}
      />
    </React.Fragment>);
}

ReportFileTable.propTypes = {
  title: PropTypes.string,
  description: PropTypes.string,
};
